// In-memory mock backend. Active when VITE_MOCK === "true" (the default), so the
// entire UI is fully clickable with zero backend. Data is deterministic per
// session and persists to localStorage so a page reload keeps your state.

import { defaultConfig } from "./defaults";
import type {
  AccountCreated,
  ApiKey,
  ApiKeyCreated,
  BoardVersion,
  Branding,
  CreateBoardInput,
  Entry,
  FlaggedScore,
  Leaderboard,
  LeaderboardConfig,
  Member,
  PlanKey,
  PlanView,
  PlayerBoard,
  PlayerSubmission,
  PlayerSummary,
  Project,
  SubmitScoreResult,
  UsageView,
  User,
  ViewToken,
} from "./types";
import { ApiError } from "./types";

const LS_KEY = "gc_mock_state_v1";

interface MockAccount {
  account_id: string;
  name: string;
  plan_key: PlanKey;
  writes_this_month: number;
}

interface MockState {
  user: User | null;
  accounts: MockAccount[];
  projects: Project[];
  members: Record<string, Member[]>;
  apiKeys: Record<string, ApiKey[]>;
  boards: Record<string, Leaderboard[]>; // by projectId
  entries: Record<string, Entry[]>; // key `${projectId}:${boardId}`
}

function seedPlayers(n: number, order: "asc" | "desc", float: boolean): Entry[] {
  const handles = [
    "nightraven", "qwop_master", "0xSpeed", "lilypad", "voidwalker",
    "ggez_no_re", "frostbyte", "marigold", "static_cling", "hexcrawler",
    "pixelsmith", "rune_07", "deadlift", "moonpenny", "turbofish",
    "owlbear", "cassette", "neon_dusk", "glasscannon", "saltcircuit",
  ];
  const countries = ["US", "JP", "DE", "BR", "KR", "SE", "GB", "CA"];
  const entries: Entry[] = handles.slice(0, n).map((h, i) => {
    const base = float ? Math.round((100 - i * 1.7) * 100) / 100 : 100_000 - i * 1234;
    return {
      rank: 0,
      player_id: h,
      score: Math.max(base + (i % 3) * 11, 1),
      subscore: [Math.round(Math.random() * 50)],
      metadata: { display_name: h, country: countries[i % countries.length] },
      updated_at: new Date(Date.now() - i * 3_600_000).toISOString(),
    };
  });
  return rerank(entries, order);
}

function rerank(entries: Entry[], order: "asc" | "desc"): Entry[] {
  const sorted = [...entries].sort((a, b) =>
    order === "desc" ? b.score - a.score : a.score - b.score,
  );
  sorted.forEach((e, i) => (e.rank = i + 1));
  return sorted;
}

function initial(): MockState {
  const user: User = {
    id: "usr_demo",
    email: "dev@studio.gg",
    name: "Demo Developer",
    email_verified: true,
    created_at: "2026-01-04T10:00:00Z",
  };
  const projects: Project[] = [
    { id: "prj_arcadia", name: "Arcadia", slug: "arcadia", role: "owner", board_count: 2, created_at: "2026-02-01T10:00:00Z" },
    { id: "prj_speedlab", name: "Speed Lab", slug: "speed-lab", role: "admin", board_count: 1, created_at: "2026-03-12T10:00:00Z" },
  ];

  const arcadiaHigh: Leaderboard = {
    ...defaultConfig("weekly-high-scores", "Weekly High Scores"),
    display_as: "numeric",
    size: 18, current_version: 1, owner_node: "node-a3",
    last_activity_at: new Date(Date.now() - 12 * 60_000).toISOString(),
    recent_updates: 142,
    created_at: "2026-02-02T10:00:00Z", updated_at: new Date().toISOString(),
  };
  const arcadiaCoins: Leaderboard = {
    ...defaultConfig("weekly-coins", "Weekly Coins"),
    update_method: "increment", display_as: "currency",
    reset: { interval: "weekly", reset_tz: "UTC" }, archive_policy: { kind: "keep_last_n", keep_last_n: 12 },
    size: 14, current_version: 4, owner_node: "node-b1",
    last_activity_at: new Date(Date.now() - 3 * 3_600_000).toISOString(),
    recent_updates: 37,
    created_at: "2026-02-10T10:00:00Z", updated_at: new Date().toISOString(),
  };
  const speedrun: Leaderboard = {
    ...defaultConfig("any-percent", "Any% Speedrun"),
    sort_order: "asc", score_type: "f64", display_as: "milliseconds",
    tiebreaker: ["subscore", "earliest_submission"], max_entries: 10_000,
    size: 12, current_version: 1, owner_node: "node-a3",
    last_activity_at: new Date(Date.now() - 2 * 24 * 3_600_000).toISOString(),
    recent_updates: 8,
    created_at: "2026-03-13T10:00:00Z", updated_at: new Date().toISOString(),
  };

  // Phase 11.4: a board GROUP (`arcade-score`) — one parent marker carrying `windows`, plus a
  // child board per window. The dashboard folds these into one card / one inspector with tabs.
  const arcadeWindows = ["all_time", "daily", "weekly", "rolling_24h"];
  const arcadeGroup: Leaderboard = {
    ...defaultConfig("arcade-score", "Arcade Score"),
    windows: arcadeWindows,
    size: 0, current_version: 1, owner_node: "node-a3",
    last_activity_at: new Date(Date.now() - 5 * 60_000).toISOString(),
    recent_updates: 0,
    created_at: "2026-04-01T10:00:00Z", updated_at: new Date().toISOString(),
  };
  const arcadeChild = (w: string, n: number): Leaderboard => ({
    ...defaultConfig(`arcade-score:${w}`, `Arcade Score — ${w}`),
    window: w.startsWith("rolling")
      ? { type: "rolling", horizon_secs: 86_400 }
      : { type: "tumbling" },
    reset: w === "daily" ? { interval: "daily", reset_tz: "UTC" }
      : w === "weekly" ? { interval: "weekly", reset_tz: "UTC" } : null,
    size: n, current_version: 1, owner_node: "node-a3",
    last_activity_at: new Date(Date.now() - 5 * 60_000).toISOString(),
    recent_updates: 60 + n,
    created_at: "2026-04-01T10:00:00Z", updated_at: new Date().toISOString(),
  });
  const arcadeChildren = arcadeWindows.map((w, i) => arcadeChild(w, 20 - i * 3));

  return {
    user,
    accounts: [
      { account_id: "acc_demo", name: "Demo Studio", plan_key: "indie", writes_this_month: 28_450 },
    ],
    projects,
    members: {
      prj_arcadia: [
        { user_id: "usr_demo", email: "dev@studio.gg", name: "Demo Developer", role: "owner" },
        { user_id: "usr_2", email: "art@studio.gg", name: "Pat Lin", role: "member" },
      ],
      prj_speedlab: [
        { user_id: "usr_demo", email: "dev@studio.gg", name: "Demo Developer", role: "admin" },
      ],
    },
    apiKeys: {
      prj_arcadia: [
        { id: "key_1", name: "Production server", prefix: "gck_live_7f3a", scopes: ["read", "write", "admin"], created_at: "2026-02-02T11:00:00Z", last_used_at: new Date(Date.now() - 90_000).toISOString(), revoked_at: null },
        { id: "key_2", name: "Analytics (read-only)", prefix: "gck_live_b1c9", scopes: ["read"], created_at: "2026-02-20T11:00:00Z", last_used_at: null, revoked_at: null },
      ],
      prj_speedlab: [],
    },
    boards: {
      prj_arcadia: [arcadeGroup, ...arcadeChildren, arcadiaHigh, arcadiaCoins],
      prj_speedlab: [speedrun],
    },
    entries: {
      "prj_arcadia:weekly-high-scores": seedPlayers(18, "desc", false),
      "prj_arcadia:weekly-coins": seedPlayers(14, "desc", false),
      "prj_speedlab:any-percent": seedPlayers(12, "asc", true),
      "prj_arcadia:arcade-score:all_time": seedPlayers(20, "desc", false),
      "prj_arcadia:arcade-score:daily": seedPlayers(17, "desc", false),
      "prj_arcadia:arcade-score:weekly": seedPlayers(14, "desc", false),
      "prj_arcadia:arcade-score:rolling_24h": seedPlayers(11, "desc", false),
    },
  };
}

function load(): MockState {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as MockState;
      // Migrate old state that may lack accounts
      if (!parsed.accounts) {
        parsed.accounts = [{ account_id: "acc_demo", name: "Demo Studio", plan_key: "indie", writes_this_month: 28_450 }];
      }
      return parsed;
    }
  } catch {
    /* ignore */
  }
  const s = initial();
  save(s);
  return s;
}

function save(s: MockState) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

let state = load();

const delay = (ms = 280) => new Promise((r) => setTimeout(r, ms));
const rid = (p: string) => `${p}_${Math.random().toString(36).slice(2, 10)}`;

function boardsOf(projectId: string): Leaderboard[] {
  return state.boards[projectId] ?? [];
}
function findBoard(projectId: string, id: string): Leaderboard {
  const b = boardsOf(projectId).find((x) => x.id === id);
  if (!b) throw new ApiError(404, "not_found", `Leaderboard '${id}' not found`);
  return b;
}

const MOCK_PLANS: PlanView[] = [
  { key: "free", name: "Free", max_projects: 1, max_boards: 10, monthly_writes: 10_000, max_ws_connections: 20, monthly_events_soft: 100_000, monthly_price_cents: 0, overage_per_million_writes_cents: null, max_entries_per_board: 10_000 },
  { key: "indie", name: "Indie", max_projects: 5, max_boards: null, monthly_writes: 5_000_000, max_ws_connections: 1_000, monthly_events_soft: 50_000_000, monthly_price_cents: 1_000, overage_per_million_writes_cents: 50, max_entries_per_board: null },
  { key: "pro", name: "Pro", max_projects: null, max_boards: null, monthly_writes: null, max_ws_connections: null, monthly_events_soft: null, monthly_price_cents: 9_900, overage_per_million_writes_cents: 40, max_entries_per_board: null },
];

export const mockApi = {
  // --- auth ---
  async login(email: string, _password: string): Promise<User> {
    await delay();
    if (!email.includes("@")) throw new ApiError(401, "invalid_credentials", "Invalid email or password");
    state.user = { ...(state.user ?? initial().user!), email };
    save(state);
    return state.user;
  },
  async register(email: string, name: string, _password: string): Promise<User> {
    await delay();
    state.user = { id: rid("usr"), email, name, email_verified: false, created_at: new Date().toISOString() };
    save(state);
    return state.user;
  },
  async me(): Promise<User> {
    await delay(120);
    if (!state.user) throw new ApiError(401, "unauthenticated", "Not signed in");
    return state.user;
  },
  async logout(): Promise<void> {
    await delay(120);
    state.user = null;
    save(state);
  },
  async updateProfile(name: string): Promise<User> {
    await delay(200);
    if (!state.user) throw new ApiError(401, "unauthenticated", "Not signed in");
    state.user = { ...state.user, name };
    save(state);
    return state.user;
  },
  async changePassword(_currentPassword: string, _newPassword: string): Promise<void> {
    await delay(400);
    // Mock: always succeeds (no real verification in mock mode).
  },

  // --- projects ---
  async listProjects(): Promise<Project[]> {
    await delay();
    return state.projects;
  },
  async createProject(name: string): Promise<Project> {
    await delay();
    const p: Project = {
      id: rid("prj"), name, slug: name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
      role: "owner", board_count: 0, created_at: new Date().toISOString(),
    };
    state.projects.push(p);
    state.boards[p.id] = [];
    state.apiKeys[p.id] = [];
    state.members[p.id] = state.user
      ? [{ user_id: state.user.id, email: state.user.email, name: state.user.name, role: "owner" }]
      : [];
    save(state);
    return p;
  },
  async updateProject(projectId: string, patch: { name?: string; branding?: Branding }): Promise<Project> {
    await delay();
    const p = state.projects.find((x) => x.id === projectId);
    if (!p) throw new ApiError(404, "not_found", "Project not found");
    if (patch.name !== undefined) p.name = patch.name;
    if (patch.branding !== undefined) p.branding = patch.branding;
    save(state);
    return p;
  },
  async listMembers(projectId: string): Promise<Member[]> {
    await delay();
    return state.members[projectId] ?? [];
  },
  async inviteMember(
    _accountId: string,
    email: string,
    role: Member["role"],
  ): Promise<Member> {
    await delay();
    // In mock mode we don't have a real user lookup; just fake a member row.
    const m: Member = {
      user_id: rid("usr"),
      email,
      name: email.split("@")[0] ?? email,
      role,
    };
    // Attach to first project (we don't have accountId→projects mapping in mock).
    const firstProjId = Object.keys(state.members)[0];
    if (firstProjId) {
      (state.members[firstProjId] ??= []).push(m);
    }
    save(state);
    return m;
  },

  // --- api keys ---
  async listKeys(projectId: string): Promise<ApiKey[]> {
    await delay();
    return state.apiKeys[projectId] ?? [];
  },
  async createKey(projectId: string, name: string, scopes: ApiKey["scopes"]): Promise<ApiKeyCreated> {
    await delay();
    const prefix = `gck_live_${Math.random().toString(16).slice(2, 6)}`;
    const secret = `${prefix}_${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
    const key: ApiKey = { id: rid("key"), name, prefix, scopes, created_at: new Date().toISOString(), last_used_at: null, revoked_at: null };
    (state.apiKeys[projectId] ??= []).push(key);
    save(state);
    return { ...key, secret };
  },
  async revokeKey(projectId: string, keyId: string): Promise<void> {
    await delay();
    const k = (state.apiKeys[projectId] ?? []).find((x) => x.id === keyId);
    if (k) k.revoked_at = new Date().toISOString();
    save(state);
  },

  // --- boards ---
  async listBoards(projectId: string): Promise<Leaderboard[]> {
    await delay();
    return boardsOf(projectId);
  },
  async getBoard(projectId: string, id: string): Promise<Leaderboard> {
    await delay();
    return findBoard(projectId, id);
  },
  async createBoard(projectId: string, cfg: CreateBoardInput): Promise<Leaderboard> {
    await delay();
    if (boardsOf(projectId).some((b) => b.id === cfg.id))
      throw new ApiError(409, "already_exists", `A board with id '${cfg.id}' already exists`);
    const now = new Date().toISOString();
    const mk = (over: Partial<Leaderboard>): Leaderboard => ({
      ...cfg, size: 0, current_version: 1, owner_node: "node-a3",
      last_activity_at: null, recent_updates: 0, created_at: now, updated_at: now,
      ...over,
    });
    const list = (state.boards[projectId] ??= []);
    const proj = state.projects.find((p) => p.id === projectId);

    // Phase 11.4: a `windows` list creates a board GROUP — a parent marker + a child per window.
    if (cfg.windows && cfg.windows.length) {
      const horizon: Record<string, number> = { rolling_24h: 86_400, rolling_7d: 604_800, rolling_30d: 2_592_000 };
      const parent = mk({ windows: cfg.windows, reset: null });
      list.push(parent);
      state.entries[`${projectId}:${cfg.id}`] = [];
      for (const w of cfg.windows) {
        const child = mk({
          id: `${cfg.id}:${w}`, name: `${cfg.name} — ${w}`, windows: undefined,
          window: w.startsWith("rolling") ? { type: "rolling", horizon_secs: horizon[w] } : { type: "tumbling" },
          reset: w === "daily" ? { interval: "daily", reset_tz: "UTC" }
            : w === "weekly" ? { interval: "weekly", reset_tz: "UTC" }
            : w === "monthly" ? { interval: "monthly", reset_tz: "UTC" } : null,
        });
        list.push(child);
        state.entries[`${projectId}:${child.id}`] = [];
      }
      if (proj) proj.board_count++;
      save(state);
      return parent;
    }

    const board = mk({});
    list.push(board);
    state.entries[`${projectId}:${cfg.id}`] = [];
    if (proj) proj.board_count++;
    save(state);
    return board;
  },
  async updateBoard(projectId: string, id: string, patch: Partial<LeaderboardConfig>): Promise<Leaderboard> {
    await delay();
    const b = findBoard(projectId, id);
    Object.assign(b, patch, { updated_at: new Date().toISOString() });
    save(state);
    return b;
  },
  async deleteBoard(projectId: string, id: string): Promise<void> {
    await delay();
    state.boards[projectId] = boardsOf(projectId).filter((b) => b.id !== id);
    delete state.entries[`${projectId}:${id}`];
    const proj = state.projects.find((p) => p.id === projectId);
    if (proj && proj.board_count > 0) proj.board_count--;
    save(state);
  },
  async listVersions(projectId: string, id: string): Promise<BoardVersion[]> {
    await delay();
    const b = findBoard(projectId, id);
    const out: BoardVersion[] = [];
    for (let v = b.current_version; v >= 1; v--) {
      out.push({
        version: v,
        archived_at: v === b.current_version ? null : new Date(Date.now() - (b.current_version - v) * 7 * 86_400_000).toISOString(),
        size: v === b.current_version ? b.size : Math.round(b.size * (0.6 + Math.random() * 0.4)),
      });
    }
    return out;
  },

  // --- entries / scores ---
  async top(projectId: string, id: string, limit = 100, offset = 0): Promise<Entry[]> {
    await delay();
    const e = state.entries[`${projectId}:${id}`] ?? [];
    return e.slice(offset, offset + limit);
  },
  async count(projectId: string, id: string): Promise<number> {
    await delay(120);
    return (state.entries[`${projectId}:${id}`] ?? []).length;
  },
  async submitScore(projectId: string, id: string, playerId: string, score: number): Promise<SubmitScoreResult> {
    await delay();
    const board = findBoard(projectId, id);
    const key = `${projectId}:${id}`;
    const list = (state.entries[key] ??= []);
    const existing = list.find((x) => x.player_id === playerId);
    const prevRank = existing?.rank ?? null;
    if (existing) {
      switch (board.update_method) {
        case "increment": existing.score += score; break;
        case "force": existing.score = score; break;
        default: {
          const better = board.sort_order === "desc" ? score > existing.score : score < existing.score;
          if (better) existing.score = score;
        }
      }
    } else {
      list.push({ rank: 0, player_id: playerId, score, metadata: { display_name: playerId } });
    }
    state.entries[key] = rerank(list, board.sort_order);
    board.size = state.entries[key].length;
    board.updated_at = new Date().toISOString();
    board.last_activity_at = new Date().toISOString();
    board.recent_updates = (board.recent_updates ?? 0) + 1;
    // Also increment account writes counter
    const acc = state.accounts[0];
    if (acc) acc.writes_this_month++;
    save(state);
    const now = state.entries[key].find((x) => x.player_id === playerId)!;
    return { rank: now.rank, score: now.score, previous_rank: prevRank, changed: true };
  },
  async deleteEntry(projectId: string, id: string, playerId: string): Promise<void> {
    await delay();
    const board = findBoard(projectId, id);
    const key = `${projectId}:${id}`;
    state.entries[key] = rerank((state.entries[key] ?? []).filter((x) => x.player_id !== playerId), board.sort_order);
    board.size = state.entries[key].length;
    save(state);
  },

  // --- moderation / anti-cheat (Gap 3) ---
  async listFlagged(_projectId: string, _status?: string): Promise<FlaggedScore[]> {
    await delay(80);
    // Mock has no anomaly pipeline; the queue is empty (the page shows its empty state).
    return [];
  },
  async resolveFlag(_projectId: string, flagId: number, status: "approved" | "rejected"): Promise<FlaggedScore> {
    await delay();
    return {
      id: flagId, project: "", leaderboard_id: "", version: 1, player_id: "", score: 0,
      reason: "", detail: {}, status, created_at: new Date().toISOString(),
    };
  },

  // --- players (player-centric view) ---
  // Derived from board entries: each board a player appears on contributes a "submission".
  async listPlayers(projectId: string, q?: string, limit?: number): Promise<PlayerSummary[]> {
    await delay(80);
    const boards = state.boards[projectId] ?? [];
    const byPlayer = new Map<string, { subs: number; boards: Set<string> }>();
    for (const b of boards) {
      for (const e of state.entries[`${projectId}:${b.id}`] ?? []) {
        const agg = byPlayer.get(e.player_id) ?? { subs: 0, boards: new Set() };
        agg.subs += 1 + (e.rank % 3); // a few submissions per standing, deterministically
        agg.boards.add(b.id);
        byPlayer.set(e.player_id, agg);
      }
    }
    const prefix = (q ?? "").toLowerCase();
    const out: PlayerSummary[] = [...byPlayer.entries()]
      .filter(([pid]) => !prefix || pid.toLowerCase().startsWith(prefix))
      .map(([player_id, a], i) => ({
        player_id,
        submission_count: a.subs,
        board_count: a.boards.size,
        last_activity: new Date(Date.now() - i * 7 * 60_000).toISOString(),
      }))
      .sort((a, b) => b.last_activity.localeCompare(a.last_activity));
    return out.slice(0, limit ?? 100);
  },
  async playerBoards(projectId: string, playerId: string): Promise<PlayerBoard[]> {
    await delay(80);
    const boards = state.boards[projectId] ?? [];
    const out: PlayerBoard[] = [];
    boards.forEach((b, i) => {
      const e = (state.entries[`${projectId}:${b.id}`] ?? []).find((x) => x.player_id === playerId);
      if (!e) return;
      const subs = 1 + (e.rank % 4);
      out.push({
        leaderboard_id: b.id,
        submission_count: subs,
        valid_count: subs,
        pending_count: 0,
        rejected_count: 0,
        latest_score: e.score,
        last_activity: new Date(Date.now() - i * 11 * 60_000).toISOString(),
      });
    });
    return out;
  },
  async playerSubmissions(
    projectId: string,
    playerId: string,
    board: string,
    limit?: number,
  ): Promise<PlayerSubmission[]> {
    await delay(80);
    const e = (state.entries[`${projectId}:${board}`] ?? []).find((x) => x.player_id === playerId);
    if (!e) return [];
    const n = Math.min(1 + (e.rank % 4), limit ?? 100);
    // Newest first; earlier attempts trend lower than the player's current best.
    return Array.from({ length: n }, (_, i) => ({
      id: 100000 - i,
      score: Math.max(Math.round(e.score * (1 - i * 0.08)), 1),
      subscores: e.subscore,
      metadata: { name: playerId },
      status: "valid" as const,
      created_at: new Date(Date.now() - i * 3 * 60_000).toISOString(),
    }));
  },
  async viewToken(_projectId: string): Promise<ViewToken> {
    await delay(40);
    // No real WS in mock mode; the Players view falls back to the ledger's latest score.
    return { token: "mock-view-token", expires_at: Math.floor(Date.now() / 1000) + 3600 };
  },

  // --- plans / billing ---
  async listPlans(): Promise<PlanView[]> {
    await delay(120);
    return MOCK_PLANS;
  },
  async getUsage(accountId: string): Promise<UsageView> {
    await delay(120);
    const acc = state.accounts.find((a) => a.account_id === accountId) ?? state.accounts[0];
    if (!acc) throw new ApiError(404, "not_found", "Account not found");
    const plan = MOCK_PLANS.find((p) => p.key === acc.plan_key) ?? MOCK_PLANS[1];
    const allBoards = Object.values(state.boards).flat();
    return {
      account_id: acc.account_id,
      period: "2026-06-01",
      plan,
      usage: {
        writes: acc.writes_this_month,
        events: 0,
        blob_bytes: 0,
        board_count: allBoards.length,
        project_count: state.projects.length,
      },
      over_write_quota: plan.monthly_writes != null && acc.writes_this_month >= plan.monthly_writes,
      over_board_limit: plan.max_boards != null && allBoards.length >= plan.max_boards,
    };
  },
  async setPlan(accountId: string, planKey: string): Promise<void> {
    await delay();
    const acc = state.accounts.find((a) => a.account_id === accountId) ?? state.accounts[0];
    if (!acc) throw new ApiError(404, "not_found", "Account not found");
    acc.plan_key = planKey as PlanKey;
    save(state);
  },
  async createAccount(name: string): Promise<AccountCreated> {
    await delay();
    const acc: MockAccount = {
      account_id: rid("acc"),
      name,
      plan_key: "free",
      writes_this_month: 0,
    };
    state.accounts.push(acc);
    save(state);
    return { account_id: acc.account_id, name: acc.name, role: "owner", plan_key: acc.plan_key };
  },

  getActiveAccountId(): string | null {
    return state.accounts[0]?.account_id ?? null;
  },

  /** test helper: reset all mock data */
  __reset() {
    state = initial();
    save(state);
  },
};
